import { z } from 'zod';
import type { PaneData } from './contracts';

/**
 * Runtime validation of a pane payload at the client boundary. The `z.ZodType<PaneData>`
 * annotation makes TypeScript verify the schema stays in lockstep with the contract —
 * a drifting field is a compile error here, not a silent `NaN` in a chart.
 */
const metricFormatSchema = z.enum(['number', 'currency', 'percent', 'duration']);

const trendPointSchema = z.object({ label: z.string(), value: z.number() });

const detailRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  value: z.string().optional(),
  meta: z.string().optional(),
  href: z.string().optional(),
  html: z.string().optional(),
  bucket: z.string().optional(),
  imageUrl: z.string().optional(),
});

export const paneDataSchema: z.ZodType<PaneData> = z.object({
  id: z.string().min(1),
  department: z.enum(['sales', 'marketing', 'support', 'admin']),
  title: z.string(),
  kind: z.enum(['metric', 'list', 'ranking', 'progress', 'agenda']),
  source: z.object({
    system: z.enum(['superoffice', 'google-analytics', 'activecampaign', 'computed']),
    label: z.string(),
    detail: z.string(),
    docUrl: z.string().optional(),
    mode: z.enum(['live', 'mock', 'stale', 'empty', 'error']),
    caveats: z.array(z.string()).optional(),
    errorReason: z.string().optional(),
    lastLiveAt: z.string().optional(),
  }),
  metric: z
    .object({
      value: z.number(),
      format: metricFormatSchema,
      unit: z.string().optional(),
      deltaPct: z.number().nullable().optional(),
      trend: z.array(trendPointSchema).optional(),
      max: z.number().optional(),
    })
    .optional(),
  rows: z.array(detailRowSchema).optional(),
  progress: z
    .object({
      current: z.number(),
      target: z.number(),
      format: metricFormatSchema,
      unit: z.string().optional(),
    })
    .optional(),
  note: z.string().optional(),
  breakdown: z.array(z.object({ label: z.string(), value: z.number() })).optional(),
  stacked: z
    .object({
      months: z.array(z.string()),
      series: z.array(z.object({ id: z.string(), name: z.string(), values: z.array(z.number()) })),
      total: z.array(z.number()),
    })
    .optional(),
  details: z.array(detailRowSchema).optional(),
  comparison: z
    .object({
      label: z.string(),
      months: z.array(z.string()),
      actual: z.array(z.number()),
      target: z.array(z.number()),
    })
    .optional(),
  table: z
    .object({
      columns: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          format: z.enum(['text', 'currency', 'number', 'percent', 'date']).optional(),
        }),
      ),
      rows: z.array(
        z.object({
          id: z.string(),
          cells: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
          href: z.string().optional(),
          group: z.string().optional(),
          drill: z.object({ kind: z.literal('ticket'), id: z.number().int() }).optional(),
          closed: z.boolean().optional(),
        }),
      ),
      totals: z.array(z.string()).optional(),
      groupLabel: z.string().optional(),
    })
    .refine((t) => !t.totals || t.columns.length === 0 || !t.totals.includes(t.columns[0]!.key), {
      message: 'totals must not include the first column — it carries the total label',
    })
    .optional(),
  periodLabel: z.string().optional(),
  updatedAt: z.string(),
}).superRefine((pane, ctx) => {
  // Only empty/error panes may be bodyless — a data-carrying pane missing its kind's
  // body would render as a silently blank card.
  if (pane.source.mode === 'empty' || pane.source.mode === 'error') return;
  if (pane.kind === 'metric' && !pane.metric) {
    ctx.addIssue({ code: 'custom', path: ['metric'], message: 'metric pane without a metric body' });
  }
  if (pane.kind === 'progress' && !pane.progress) {
    ctx.addIssue({ code: 'custom', path: ['progress'], message: 'progress pane without a progress body' });
  }
  if ((pane.kind === 'list' || pane.kind === 'ranking' || pane.kind === 'agenda') && !pane.rows) {
    ctx.addIssue({ code: 'custom', path: ['rows'], message: `${pane.kind} pane without rows` });
  }
});

/** Parses an untrusted pane payload; throws a ZodError on a malformed response. */
export function parsePaneData(json: unknown): PaneData {
  return paneDataSchema.parse(json);
}
